import {businessOperationTypes} from "@common-core/coat-operational-hierarchy-appsync-model";
import {BoidAssignmentState} from "../model";

/**
 * Represents a group of BOID assignment states organized by business operation type.
 * This structure is designed for UI components that need to display assignment states
 * grouped by type with display-friendly names.
 */
export interface BoidAssignmentStateGroup {
  type: string;
  name: string;
  states: BoidAssignmentState[];
}

/**
 * Groups BOID assignment states by their business operation type, ensuring that
 * all known business operation types are represented even if they have no
 * assignment states.
 *
 * This function creates a complete list of type groups by:
 * 1. Grouping the provided assignment states by their type (normalized to uppercase)
 * 2. Creating a group for each business operation type from the appsync-model
 * 3. Ensuring each type has a group, even if no assignment states match that type
 *
 * This guarantees that UI components will display all available business operation
 * types, showing "no assignments" for types without any BOID assignment states.
 *
 * @param boidAssignmentStates - Array of BOID assignment states to group by type
 * @returns Array of groups containing type, display name, and associated states
 *
 * @see {@link businessOperationTypes} for the complete list of supported types
 * @see {@link BoidAssignmentState} for the structure of individual assignment states
 */
export const groupBoidAssignmentStatesByType = (
  boidAssignmentStates: BoidAssignmentState[]
): BoidAssignmentStateGroup[] => {
  const statesByType = boidAssignmentStates.reduce(
    (acc, state) => {
      const type = state.type.toUpperCase();
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(state);
      return acc;
    },
    {} as Record<string, BoidAssignmentState[]>
  );

  return businessOperationTypes().map(type => ({
    type,
    name: type,
    states: statesByType[type.toUpperCase()] || []
  }));
};
